import { BiSearchIconInput } from "../../primitives/inputs/BiSearchIconInput.primitive";
import InfiniteScroll from "react-infinite-scroll-component";
import styled, { css } from "styled-components/macro";
import DropdownSvg from "../../../assets/dropdown.svg";
import { Label } from "../../../styles/StyledComponents";
import Asterix from "../../../assets/asterix.svg";

import Check from "../../../assets/check.svg";
import { useEffect, useRef, useState } from "react";
import { debounce } from "lodash";
import useOutsideClick from "../../../hooks/useOutsideClick";
import { useTranslation } from "react-i18next";
import { compact, isObject } from "lodash";
import { useAppDispatch } from "../../../app/hooks";
import { setToastData } from "../../../app/toastReducer";
import { AppConstants } from "../../../constants/AppConstants";
import { device } from "../../../styles/mediaQueries";
import { useLocalization } from "../../../hooks/useLocalization";

export const enum PICKLIST_TYPES {
  SINGLE = 0,
  MULTI = 1,
}

interface PickListProps {
  type: number;
  fieldKey: string;
  fieldValues: Array<string>;
  items: Array<any>;
  selectedItems: Array<any>;
  onSelectedChange: Function;
  disabled: boolean;
  readOnly: boolean;
  label?: string;
  required?: boolean;
  fetchMoreData?: Function;
  hasMore?: boolean;
  searchPlaceholder?: string;
  searchable?: boolean;
  searchData?: Function;
  containerHeight?: string;
  onlySearch?: boolean;
  infoIcon?: any;
  placeholder?: string;
}

function PickList({
  fieldKey,
  fieldValues = [],
  items = [],
  selectedItems = [],
  onSelectedChange,
  disabled = false,
  readOnly = false,
  type = PICKLIST_TYPES.SINGLE,
  label,
  required = false,
  fetchMoreData = () => {},
  searchData = () => {},
  hasMore = false,
  searchPlaceholder = "Search",
  searchable = false,
  containerHeight = "",
  onlySearch = false,
  infoIcon,
  placeholder = "",
}: PickListProps) {
  const selectRef = useRef(null);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { getRTLLayout } = useLocalization();
  const rtlClasses = getRTLLayout();

  const [keyValues, setKeyValues] = useState<any>({});
  const [searchText, setSearchText] = useState("");

  const setSelectedItems = () => {
    let keyValues = {};
    if (selectedItems.length) {
      keyValues = selectedItems.reduce((acc: any, elem: any) => {
        acc[elem[fieldKey]] = elem;
        return acc;
      }, {});
    }

    return setKeyValues(keyValues);
  };

  useEffect(() => {
    setSelectedItems();
  }, [selectedItems]);

  const [showPopup, setShowPopup] = useState(false);

  const toggling = () => {
    setShowPopup(!showPopup);
  };

  useOutsideClick(
    selectRef,
    () => {
      if (showPopup) {
        setShowPopup(false);
      }
    },
    false
  );
  const onOptionClicked = (item: any) => () => {
    if (!item || readOnly) {
      return;
    }

    let newKeyValues = { ...keyValues };
    // const selection: any = isObject(item.value) ? item.value : item;
    if (newKeyValues[item[fieldKey]]) {
      delete newKeyValues[item[fieldKey]];
    } else {
      if (!type) {
        newKeyValues = {};
      }
      newKeyValues[item[fieldKey]] = item;
    }
    onSelectedChange(Object.values(newKeyValues));
    setKeyValues(newKeyValues);

    if (!type) {
      setShowPopup(false);
    }
  };

  const getDisplayValue = (item: any = {}) =>
    compact(fieldValues.map((fieldValue: string) => item[fieldValue])).join();

  let selection: string = t("T_SELECT");
  const length = Object.keys(keyValues).length;

  if (length) {
    selection = type
      ? `${length}` + t("T_ITEMS_SELECTED")
      : Object.values(keyValues)
          .map((item: any) =>
            getDisplayValue(isObject(item.value) ? item.value : item)
          )
          .join();
  }

  const fetchMoreDataFunc = () => {
    fetchMoreData();
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    if (e.target.value.length < AppConstants.DEFAULT_TEXT_MAX_LENGTH) {
      debouncedSearch(e.target.value);
    } else {
      dispatch(
        setToastData({
          toastMessage: t("T_SEARCH_ERROR"),
          isToastActive: true,
          toastType: "error",
        })
      );
    }
  };

  const debouncedSearch = useRef(
    debounce(async (data) => {
      if (data && data.trim()) {
        searchData(data.trim());
      } else if (!data) {
        searchData(data);
      }
    }, 400)
  ).current;

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  return (
    <>
      {label && (
        <Label>
          {required ? <Image className={rtlClasses.marginRight("1")} src={Asterix} /> : ""}
          {t(label)}
        </Label>
      )}

      <DropDownContainer className={disabled ? "disabled" : ""}>
        <DropDownHeader onClick={toggling} data-testid="select-field-header">
          {infoIcon ? (
            <img
              src={infoIcon}
              className={`${rtlClasses.marginRight("1.5")} hidden h-6 w-6 md:block`}
            ></img>
          ) : (
            ""
          )}
          {selection === t("T_SELECT") ? (
            placeholder || selection
          ) : (
            <Divtext title={selection}>{selection}</Divtext>
          )}
          {!disabled ? <img src={DropdownSvg} className={rtlClasses.marginLeft("auto")} /> : ""}
        </DropDownHeader>
        {showPopup && (
          <DropDownListContainer
            ref={selectRef}
            data-testid="select-option-list"
          >
            <DropDownList className={`${containerHeight}`}>
              {searchable && (
                <SearchPickListItem
                  onClick={onOptionClicked("")}
                  data-testid="search-field"
                >
                  {" "}
                  <BiSearchIconInput
                    isEmailTemplate
                    autoFocus
                    value={searchText}
                    placeholder={searchPlaceholder}
                    onChange={handleSearchChange}
                  />
                </SearchPickListItem>
              )}

              {!searchable && (
                <PickListItem onClick={onOptionClicked("")}>
                  {t("T_SELECT")}
                </PickListItem>
              )}
              {!hasMore && !items.length && (
                <PickListItem onClick={onOptionClicked("")}>
                  {t("T_NO_DATA_AVAILABLE_MSG")}
                </PickListItem>
              )}
              <InfiniteScrollOuterDiv>
                <InfiniteScroll
                  dataLength={items.length}
                  next={fetchMoreDataFunc}
                  hasMore={hasMore}
                  loader={searchText.length ? <h4>Loading...</h4> : <></>}
                  height={
                    hasMore && (items.length || !onlySearch) ? 280 : "auto"
                  }
                >
                  {items.map((item: any) => (
                    <PickListItem
                      onClick={onOptionClicked(item)}
                      key={item.key}
                      data-testid="select-option-item"
                    >
                      {item.icon ? (
                        <div className={`${rtlClasses.marginRight("2")} self-center`}>{item.icon}</div>
                      ) : (
                        ""
                      )}
                      <Divtext
                        title={getDisplayValue(
                          isObject(item.value) ? item.value : item
                        )}
                      >
                        {getDisplayValue(
                          isObject(item.value) ? item.value : item
                        )}
                      </Divtext>
                      {!!keyValues[item[fieldKey]] && (
                        <ImageContainer>
                          <CheckMark src={Check} />
                        </ImageContainer>
                      )}
                    </PickListItem>
                  ))}
                </InfiniteScroll>
              </InfiniteScrollOuterDiv>

              {/* {items.map((item: any) => (
                <PickListItem
                  onClick={onOptionClicked(item)}
                  key={item.key}
                  data-testid="select-option-item"
                >
                  <ImageContainer>
                    {!!keyValues[item[fieldKey]] && <CheckMark src={Check} />}
                  </ImageContainer>
                  <Text>{getDisplayValue(isObject(item.value) ? item.value : item)}</Text>
                </PickListItem>
              ))} */}
            </DropDownList>
          </DropDownListContainer>
        )}
      </DropDownContainer>
    </>
  );
}

export const Divtext = styled("div")`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  overflow: hidden;
  justify-content: flex-start;
  flex-flow: row wrap;
  // flex: 1 0 0;
  word-break: break-all;
`;

export const DropDownContainer = styled("div")`
  /* Auto layout */

  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 10px 12px;
  width: 100%;
  height: 44px;
  background: #ffffff;

  border: 0.5px solid #8c8c8c;
  box-sizing: border-box;
  border-radius: 4px;
  position: relative;
  cursor: pointer;
  &.disabled {
    pointer-events: none;
    background-color: #f5f5f5;
  }

  @media ${device.tablet} {
    padding: 0px 10px;
    align-self: end;
  }
`;

export const DropDownHeader = styled("div")`
  width: 100%;
  display: flex;
  align-items: center;
  // justify-content: space-between;

  @media ${device.tablet} {
    font-size: 14px;
    line-height: 20px;
  }
`;

export const InfiniteScrollOuterDiv = styled("div")`
  width: 100%;
`;

export const DropDownListContainer = styled("div")``;

export const DropDownList = styled("ul")`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 1px 0px;
  position: absolute;
  width: 100%;
  max-height: 280px;
  z-index: 1;
  left: 0;
  top: 45px;
  background: #ffffff;
  border: 1px solid #bfbfbf;
  box-shadow: 0px 9px 28px 8px rgba(0, 0, 0, 0.05),
    0px 6px 16px rgba(0, 0, 0, 0.08), 0px 3px 6px -4px rgba(0, 0, 0, 0.12);
  border-radius: 6px;
  overflow-y: auto;
`;

export const picklistStyled = css`
  display: flex;
  flex: 1 1 0;
  flex-flow: row wrap;
  align-items: center;
  height: 1.5rem

  list-style: none;
  font-family: 'Open Sans';
  font-size: 14px;
  line-height: 22px;
  color: #262626;
  padding: 9px 10px;
  cursor: pointer;
  width: 100%;

  @media ${device.tablet} {
    font-size: 12px;
    line-height: 18px;
    padding: 6px 8px;
  }
`;

export const PickListItem = styled("li")`
  ${picklistStyled}
  &:hover {
    color: white;
    background: var(--color-bi-gray-700);
  }
`;

export const SearchPickListItem = styled("li")`
  ${picklistStyled}
`;

export const ImageContainer = styled.div`
  width: 1.5rem;
  height: 1rem;
  display: flex;
  justify-content: flex-start;
  color: green;
`;

export const CheckMark = styled.img`
  width: 1.5rem;
  height: 1rem;
`;

export const Text = styled.div`
  display: flex;
  flex: 1 1 0;
  justify-content: flex-start;
  flex-flow: row wrap;
`;

export const Image = styled.img`
  width: 8px;
  height: 8px;
`;

export default PickList;
